import type React from 'react';
import { type FC, useRef } from 'react';

import type { SubmitPhase } from '../../../hooks/useCancellationSubmit';
import type { CapabilitiesOutcome, ICancellationRow } from '../../../lib/cancellation-api';
import {
	canRetryEvidence,
	type ICancellationRecord,
	type ICancellationSnapshot,
	type IEvidenceItem,
} from '../../../lib/cancellation-store';
import { COPY, type WithdrawOutcome } from '../../../lib/cancellation-sync';
import { isStoragePaused } from '../../../lib/media-relay';
import { CancelledStamp, clockTime, evidenceAllowed } from './shared';
import WithdrawRing from './withdraw-ring';

type StageTone = 'done' | 'active' | 'wait' | 'problem';

interface IStage {
	tone: StageTone;
	text: string;
	action?: { label: string; run: () => void };
}

const KIND_LABEL: Record<IEvidenceItem['kind'], string> = { Image: 'Photo', Audio: 'Voice note', Video: 'Video' };

/** Said for a file the server didn't take yet — never a dead end. */
export const SAVED_FOR_LATER = 'Saved on this phone — it will upload when your party’s server accepts files.';

const offline = () => typeof navigator !== 'undefined' && !navigator.onLine;

/** Stage 2 — the party alert. Honest about where the report actually is. */
export const reportStage = (record: ICancellationRecord | null, row: ICancellationRow | null, phase: SubmitPhase): IStage => {
	// A row that is not this record's — typically the cached server row of an EARLIER report on
	// the unit that was withdrawn — says nothing about a newer report still on the phone.
	if (record && row && !record.withdrawnAt && record.report.state !== 'sent' && record.row?.id !== row.id) row = null;
	if (row?.state === 'Withdrawn') return { tone: 'done', text: 'Withdrawn.' };
	if (row?.state === 'Reinstated') return { tone: 'done', text: 'Your party reinstated this unit.' };
	if (!record) {
		return row
			? { tone: 'done', text: `Recorded by ${row.recordedBy || 'your party'} at ${clockTime(row.recordedAt)}.` }
			: { tone: 'wait', text: '' };
	}
	const r = record.report;
	if (record.withdrawnAt && r.state === 'cancelled') return { tone: 'done', text: 'Withdrawn.' };
	switch (r.state) {
		case 'sent':
			return record.alreadyCancelled
				? { tone: 'done', text: 'Already reported for this unit.' }
				: { tone: 'done', text: `Party alerted at ${clockTime(record.sentAt)}.` };
		case 'refused':
			return { tone: 'problem', text: r.error || 'The server refused this report.' };
		case 'held':
			return { tone: 'problem', text: r.error || 'Couldn’t send after several tries.' };
		default:
			if (offline() || r.attempts > 0) return { tone: 'wait', text: 'Waiting for signal — sends automatically.' };
			return { tone: 'active', text: phase === 'saving' ? 'Saving…' : 'Sending to your party…' };
	}
};

/** Stage 3 — evidence, counted honestly: only what the server has registered counts. */
export const evidenceStage = (record: ICancellationRecord | null): IStage | null => {
	const items = record?.evidence ?? [];
	if (!items.length) return null;
	const done = items.filter((e) => e.state === 'registered').length;
	if (items.every((e) => e.state === 'unsupported')) {
		return { tone: 'wait', text: 'Evidence saved on this phone — it will upload when your party’s server accepts files.' };
	}
	if (done === items.length) return { tone: 'done', text: `Evidence ${done} of ${items.length} uploaded.` };
	// The server can't store files right now — say so plainly; the files wait on this phone.
	if (items.some((e) => e.state !== 'registered' && isStoragePaused(e.code))) {
		return { tone: 'wait', text: `Evidence ${done} of ${items.length} uploaded. ${COPY.evidencePaused}` };
	}
	const problem = items.some((e) => e.state === 'held' || e.state === 'rejected' || e.state === 'missing');
	const uploading = items.some((e) => e.state === 'uploading');
	return { tone: problem ? 'problem' : uploading ? 'active' : 'wait', text: `Evidence ${done} of ${items.length} uploaded.` };
};

const itemStatus = (e: IEvidenceItem, percent?: number): string => {
	if ((e.state === 'queued' || e.state === 'held') && isStoragePaused(e.code))
		return 'Paused on the server — saved on this phone';
	switch (e.state) {
		case 'registered':
			return 'Uploaded';
		case 'uploading':
			return percent != null ? `Uploading ${percent}%` : 'Uploading…';
		case 'uploaded':
			return 'Saving…';
		case 'held':
			return 'Couldn’t upload — tap Retry';
		case 'rejected':
			return e.error || 'Not accepted';
		case 'missing':
			return 'Re-attach evidence';
		case 'unsupported':
			return SAVED_FOR_LATER;
		default:
			return 'Waiting to upload';
	}
};

const StageDot: FC<{ tone: StageTone }> = ({ tone }) => (
	<span
		className={`pc-stage__dot pc-stage__dot--${tone}`}
		aria-hidden='true'
	>
		{tone === 'done' ? (
			<svg
				width='10'
				height='10'
				viewBox='0 0 12 12'
				fill='none'
			>
				<path
					d='M2.5 6.2 5 8.5l4.5-5'
					stroke='currentColor'
					strokeWidth='1.8'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
			</svg>
		) : null}
	</span>
);

interface IReceiptProps {
	record: ICancellationRecord | null;
	serverRow: ICancellationRow | null;
	snapshot: ICancellationSnapshot;
	caps: CapabilitiesOutcome | null;
	phase: SubmitPhase;
	progress: Record<string, number>;
	discreet: boolean;
	setDiscreet: (on: boolean) => void;
	onRetry: () => void;
	onWithdraw: () => Promise<WithdrawOutcome>;
	onAddEvidence: () => void;
	onReattach: (evidenceId: string, file: File) => void;
	/** Requeue one refused or parked file and send it now. */
	onRetryEvidence: (evidenceId: string) => void;
	onDone: () => void;
	now?: () => number;
}

export const Receipt: FC<IReceiptProps> = ({
	record,
	serverRow,
	snapshot,
	caps,
	phase,
	progress,
	discreet,
	setDiscreet,
	onRetry,
	onWithdraw,
	onAddEvidence,
	onReattach,
	onRetryEvidence,
	onDone,
	now = Date.now,
}) => {
	const reattachRef = useRef<HTMLInputElement>(null);
	const reattachIdRef = useRef<string | null>(null);

	// The snapshot already prefers a newer on-phone report over an older cached server row;
	// falling back to `serverRow` here would resurrect that older row (e.g. "Withdrawn.").
	const row = (record ? (snapshot.row ?? record.row) : (snapshot.row ?? serverRow)) ?? null;
	const savedAt = record ? clockTime(record.createdAt) : '';
	const stage2 = reportStage(record, row, phase);
	const stage3 = evidenceStage(record);
	const retryable =
		record &&
		(record.report.state === 'held' ||
			record.evidence.some((e) => e.state === 'held') ||
			(record.report.state === 'queued' && !offline()) ||
			record.incident.incidentState === 'held');

	// Withdraw: a report still on the phone can always be pulled back; a sent one only inside
	// the server's window, and only the agent's own unreviewed row.
	const localOnly = !!record && record.report.state !== 'sent' && record.report.state !== 'refused' && !record.withdrawnAt;
	const ownAgentRow =
		!!row && !record?.alreadyCancelled && !record?.baseline && row.source === 'Agent' && (row.state ?? 'Active') === 'Active';
	const windowMinutes = caps?.mode === 'v2' ? caps.caps.withdrawWindowMinutes : 10;
	const deadline = row?.withdrawableUntil
		? Date.parse(row.withdrawableUntil)
		: row && row.reviewStatus !== 'Confirmed' && windowMinutes > 0
			? Date.parse(row.recordedAt) + windowMinutes * 60_000
			: NaN;
	const reviewed = row?.reviewStatus === 'Confirmed';

	const pickReattach = (id: string) => {
		reattachIdRef.current = id;
		reattachRef.current?.click();
	};
	const onReattachFile = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const file = target.files?.[0];
		target.value = '';
		if (file && reattachIdRef.current) onReattach(reattachIdRef.current, file);
	};

	return (
		<section
			className='pc-step pc-receipt'
			aria-labelledby='pc-receipt-title'
		>
			<h4
				id='pc-receipt-title'
				className='visually-hidden'
			>
				Report status
			</h4>
			{snapshot.active ? (
				<CancelledStamp
					reason={snapshot.reason}
					cancelledAt={snapshot.cancelledAt}
					discreet={discreet}
					localOnly={snapshot.localOnly}
				/>
			) : null}
			{reviewed && snapshot.active ? <span className='pc-reviewed'>Reviewed by party</span> : null}

			<ol
				className='pc-stages'
				aria-live='polite'
			>
				{record ? (
					<li className='pc-stage'>
						<StageDot tone='done' />
						<span>Saved on this phone{savedAt ? ` at ${savedAt}` : ''}.</span>
					</li>
				) : null}
				{stage2.text ? (
					<li className={`pc-stage pc-stage--${stage2.tone}`}>
						<StageDot tone={stage2.tone} />
						<span>{stage2.text}</span>
					</li>
				) : null}
				{stage3 ? (
					<li className={`pc-stage pc-stage--${stage3.tone}`}>
						<StageDot tone={stage3.tone} />
						<div className='pc-stage__body'>
							<span>{stage3.text}</span>
							<ul className='pc-ev-list'>
								{record!.evidence.map((e) => {
									const percent = progress[e.id];
									return (
										<li
											key={e.id}
											className={`pc-ev-item pc-ev-item--${e.state}`}
										>
											<span className='pc-ev-item__kind'>{KIND_LABEL[e.kind]}</span>
											<span className='pc-ev-item__status'>{itemStatus(e, percent)}</span>
											{e.state === 'uploading' && percent != null ? (
												<span
													className='pc-ev-item__bar'
													style={{ width: `${percent}%` }}
													aria-hidden='true'
												/>
											) : null}
											{e.state === 'missing' ? (
												<button
													type='button'
													className='pc-link'
													onClick={() => pickReattach(e.id)}
												>
													Re-attach
												</button>
											) : null}
											{canRetryEvidence(e) ? (
												<button
													type='button'
													className='pc-link'
													onClick={() => onRetryEvidence(e.id)}
													disabled={phase === 'sending' || phase === 'saving'}
													aria-label={`Try again: ${KIND_LABEL[e.kind]}`}
												>
													Try again
												</button>
											) : null}
										</li>
									);
								})}
							</ul>
						</div>
					</li>
				) : null}
			</ol>
			<input
				ref={reattachRef}
				type='file'
				accept='image/*,video/*,audio/*'
				className='d-none'
				onChange={onReattachFile}
			/>

			{retryable ? (
				<button
					type='button'
					className='pc-btn pc-btn--ghost'
					onClick={onRetry}
					disabled={phase === 'sending' || phase === 'saving'}
				>
					{phase === 'sending' ? 'Sending…' : 'Retry now'}
				</button>
			) : null}

			{localOnly ? (
				<WithdrawLocal onWithdraw={onWithdraw} />
			) : ownAgentRow && row?.reviewStatus !== 'Confirmed' && Number.isFinite(deadline) ? (
				<WithdrawRing
					deadline={deadline}
					windowMs={windowMinutes * 60_000}
					onWithdraw={onWithdraw}
					now={now}
				/>
			) : ownAgentRow && reviewed ? (
				<p className='pc-withdraw__closed'>Your party has reviewed this report. Ask your party admin if INEC reverses it.</p>
			) : null}

			<div className='pc-receipt__actions'>
				{snapshot.active && !localOnly && row && !record?.baseline && evidenceAllowed(caps) ? (
					<button
						type='button'
						className='pc-btn pc-btn--ghost'
						onClick={onAddEvidence}
					>
						Add evidence
					</button>
				) : null}
				<button
					type='button'
					className='pc-btn pc-btn--primary'
					onClick={onDone}
				>
					Done
				</button>
			</div>

			<label className='pc-discreet'>
				<input
					type='checkbox'
					checked={discreet}
					onChange={(e) => setDiscreet(e.target.checked)}
				/>
				<span>Discreet mode — no colour on the stamp</span>
			</label>
		</section>
	);
};

const WithdrawLocal: FC<{ onWithdraw: () => Promise<WithdrawOutcome> }> = ({ onWithdraw }) => (
	<WithdrawConfirm
		label='Withdraw'
		onWithdraw={onWithdraw}
	/>
);

const WithdrawConfirm: FC<{ label: string; onWithdraw: () => Promise<WithdrawOutcome> }> = ({ label, onWithdraw }) => {
	const ref = useRef<HTMLDetailsElement>(null);
	return (
		<details
			className='pc-withdraw pc-withdraw--local'
			ref={ref}
		>
			<summary className='pc-withdraw__btn'>{label}</summary>
			<div className='pc-withdraw--confirm'>
				<p className='pc-withdraw__q'>Withdraw this report? It hasn’t reached your party yet.</p>
				<div className='pc-withdraw__actions'>
					<button
						type='button'
						className='pc-btn pc-btn--quiet-danger'
						onClick={() => void onWithdraw()}
					>
						Withdraw
					</button>
					<button
						type='button'
						className='pc-btn pc-btn--ghost'
						onClick={() => ref.current?.removeAttribute('open')}
					>
						Keep
					</button>
				</div>
			</div>
		</details>
	);
};
